import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTasksListQuery } from '@/modules/common/queries/tasks.ts';
import FormSelectPaginate from '@/modules/admin-dashboard/components/FormSelectPaginate.tsx';
import FormsItemsSelectPaginate from '@/modules/admin-dashboard/components/FormsItemsSelectPaginate.tsx';
import RegisterSelectPaginate from '@/modules/admin-dashboard/components/RegisterSelectPaginate.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import FormItemComplianceProgressItem from '@/modules/admin-dashboard/components/items/FormItemComplianceProgressItem.tsx';
import { useTaskResultsItemsQuery } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  BaseModalProps,
  EmptyListState,
  FilterItem,
  ModalContentWrapper,
  ModalProviderContext,
  SelectPaginateOption,
  Spinner,
} from 'ui';
import { RegisterResult, TaskListMode } from 'compliance-api';

const basicSelectFilterItemClass =
  "[&_[class*='control']]:!p-0 [&_[class*='IndicatorsContainer']]:flex-row-reverse";

const registerComplianceResultOptions = Object.values(RegisterResult).map(value => ({
  label: capitalize(lowerCase(value)),
  value,
}));

interface Filters {
  user?: SelectPaginateOption<number>;
  form?: SelectPaginateOption<number>;
  result?: RegisterResult;
  register?: SelectPaginateOption<number>;
  taskId?: number;
  question?: SelectPaginateOption<string>;
}

interface ComplianceProgressModalProps extends BaseModalProps {
  initRegister?: SelectPaginateOption<number>;
  initResult?: RegisterResult;
  fromDate?: string;
  toDate?: string;
}

const ComplianceProgressModal = ({
  open,
  onClose,
  initRegister,
  initResult,
  fromDate,
  toDate,
}: ComplianceProgressModalProps) => {
  const { modals } = useContext(ModalProviderContext);
  const [filters, setFilters] = useState<Filters>({ register: initRegister, result: initResult });

  const { data: tasksData } = useTasksListQuery({
    page: 1,
    page_size: 1000, // todo: fix after fixing on the backend
    mode: TaskListMode.History,
  });

  const {
    data: itemsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useTaskResultsItemsQuery({
    page: 1,
    page_size: 15,
    form_id: filters.form?.value,
    task_id: filters.taskId,
    question_id: filters.question?.value,
    register_id: filters.register?.value,
    register_result: filters.result,
    user_id: filters.user?.value,
    from_date: fromDate ? dayjs(fromDate).startOf('day').toISOString() : undefined,
    to_date: toDate ? dayjs(toDate).endOf('day').toISOString() : undefined,
  });

  const items = useMemo(
    () => (itemsData?.pages || []).flatMap(({ data }) => data),
    [itemsData?.pages],
  );

  const tasksOptions = useMemo(() => {
    return (tasksData?.pages || [])
      .flatMap(({ data }) => data)
      .map(task => ({
        label: task.name,
        value: task.id,
      }));
  }, [tasksData?.pages]);

  const handleChangeUserFilter = useCallback((user: SelectPaginateOption<number> | null) => {
    setFilters(filters => (user ? { ...filters, user } : omit(filters, ['user'])));
  }, []);

  const handleChangeRegisterFilter = useCallback(
    (register: SelectPaginateOption<number> | null) => {
      setFilters(filters => (register ? { ...filters, register } : omit(filters, ['register'])));
    },
    [],
  );

  const handleChangeQuestionFilter = useCallback(
    (question: SelectPaginateOption<string> | null) => {
      setFilters(filters => (question ? { ...filters, question } : omit(filters, ['question'])));
    },
    [],
  );

  const handleChangeFormFilter = useCallback((form: SelectPaginateOption<number> | null) => {
    setFilters(filters => (form ? { ...filters, form } : omit(filters, ['form'])));
  }, []);

  const handleChangeTaskFilter = useCallback((taskId: number | null) => {
    setFilters(filters => (taskId ? { ...filters, taskId } : omit(filters, ['taskId'])));
  }, []);

  const handleChangeComplianceStatusFilter = useCallback((result: RegisterResult | null) => {
    setFilters(filters => (result ? { ...filters, result } : omit(filters, ['result'])));
  }, []);

  return (
    <ModalContentWrapper
      title="Compliance Questions Answers"
      variant="right"
      onClose={modals.length > 1 ? () => null : onClose}
      open={open}
      classes={{ children: 'p-0' }}
    >
      <div className="flex flex-wrap gap-2.5 p-6 border-b">
        <FilterItem
          type="custom"
          classes={{
            element: 'min-w-[125px] [&>div>span]:w-full [&_*]:!border-none w-full !py-0 !pr-0.5',
          }}
        >
          <UsersSelectPaginated
            isClearable
            isMulti={false}
            placeholder="User"
            value={filters.user}
            onChange={handleChangeUserFilter}
            className={classNames(
              basicSelectFilterItemClass,
              "[&_[class*='singleValue']]:before:content-['User:_']",
            )}
          />
        </FilterItem>
        <FilterItem
          type="custom"
          classes={{
            element: 'min-w-[125px] [&>div>span]:w-full [&_*]:!border-none w-full !py-0 !pr-0.5',
          }}
        >
          <FormSelectPaginate
            isClearable
            formFieldTitle=""
            isMulti={false}
            placeholder="Form"
            onChange={handleChangeFormFilter}
            className={classNames(
              basicSelectFilterItemClass,
              "[&_[class*='singleValue']]:before:content-['Form:_']",
            )}
          />
        </FilterItem>
        <FilterItem
          type="select"
          placeholder="Task"
          value={filters.taskId}
          options={tasksOptions}
          onChange={handleChangeTaskFilter}
          classes={{
            element: classNames(
              basicSelectFilterItemClass,
              "min-w-[125px] [&>div]:!min-w-0 [&_[class*='singleValue']]:before:content-['Task:_']",
            ),
          }}
        />
        <FilterItem
          type="custom"
          classes={{
            element: 'min-w-[125px] [&>div>span]:w-full [&_*]:!border-none w-full !py-0 !pr-0.5',
          }}
        >
          <FormsItemsSelectPaginate
            isClearable
            isMulti={false}
            placeholder="Question"
            value={filters.question}
            onChange={handleChangeQuestionFilter}
            className={classNames(
              basicSelectFilterItemClass,
              "[&_[class*='singleValue']]:before:content-['Question:_']",
            )}
          />
        </FilterItem>
        <FilterItem
          type="select"
          value={filters.result}
          placeholder="Compliance status"
          options={registerComplianceResultOptions}
          onChange={handleChangeComplianceStatusFilter}
          classes={{
            element: classNames(
              basicSelectFilterItemClass,
              "[&_[class*='singleValue']]:before:content-['Compliance_status:_'] [&>div]:!min-w-0",
            ),
          }}
        />
        <FilterItem
          type="custom"
          classes={{
            element: 'min-w-[125px] [&>div>span]:w-full [&_*]:!border-none w-full !py-0 !pr-0.5',
          }}
        >
          <RegisterSelectPaginate
            isClearable
            isMulti={false}
            placeholder="Label"
            value={filters.register}
            onChange={handleChangeRegisterFilter}
            className={classNames(
              basicSelectFilterItemClass,
              "[&_[class*='singleValue']]:before:content-['Label:_']",
            )}
          />
        </FilterItem>
      </div>
      <div className="p-6">
        {isLoading ? (
          <div className="w-full h-[250px] flex justify-center items-center">
            <Spinner size="md" />
          </div>
        ) : (
          <InfiniteScroll
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={
              <div className="w-full h-[120px] flex justify-center items-center">
                <Spinner size="md" />
              </div>
            }
            dataLength={items.length}
            className="space-y-6"
            scrollableTarget="modal_scroll"
          >
            {items.map((item, index) => (
              <FormItemComplianceProgressItem key={index + item.id} item={item} />
            ))}
          </InfiniteScroll>
        )}
        <EmptyListState
          title="No items found"
          description="This displays form answers that are compliant with a specific label."
          isLoading={isLoading}
          itemsCount={items.length}
        />
      </div>
    </ModalContentWrapper>
  );
};
export default ComplianceProgressModal;
